import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { EMPTY, Observable } from 'rxjs';
import { delay, expand, filter, map, mapTo } from 'rxjs/operators';

import {
    ExportJobId,
    ExportRequest,
    ExportStatus,
    IExportJob,
    PaginatedResults,
    PaginationInfo,
    ShPaginationService,
    makeSequentialRequests,
    mapToPaginatedResult
} from '@stylehaven/components';
import { ExportJob } from '../../entities/mediahaven-api/export-job.entity';
import { HttpService } from '../http/http.service';

@Injectable({ providedIn: 'root' })
export class ExportsService {
    private readonly defaultHeaders: HttpHeaders = new HttpHeaders();
    private readonly exportsEndpoint: string = `v2/exports`;

    constructor(
        protected http: HttpService,
        private readonly paginationService: ShPaginationService
    ) {}

    public getExportJob(exportJobId: string): Observable<ExportJob> {
        const request = `${this.exportsEndpoint}/${exportJobId}`;

        return this.http.get<IExportJob>(request).pipe(map(result => new ExportJob(result)));
    }

    public getExportJobs(
        paginationInfo: PaginationInfo,
        userId: string,
        tag: string,
        partialTagSearch: boolean
    ): Observable<PaginatedResults<ExportJob>> {
        const tagString: string = partialTagSearch ? `*${tag}*` : tag;
        const startIndex: number = paginationInfo.page * paginationInfo.resultsPerPage;
        const httpParams: HttpParams = new HttpParams().append('userId', userId).append('tag', tagString);

        return this.paginationService
            .get<ExportJob>(
                this.exportsEndpoint,
                httpParams,
                this.defaultHeaders,
                startIndex,
                paginationInfo.resultsPerPage
            )
            .pipe(
                map(results => ({
                    ...results,
                    Results: results.Results.map(exportJob => new ExportJob(exportJob))
                }))
            )
            .pipe(map(results => mapToPaginatedResult(results, paginationInfo.resultsPerPage)));
    }

    public exportRecords(exportRequest: ExportRequest): Observable<IExportJob[]> {
        return this.http.post<IExportJob[]>(this.exportsEndpoint, exportRequest);
    }

    public deleteExportJob(exportJob: ExportJob): Observable<boolean> {
        const exportJobId: ExportJobId = exportJob.ExportJobId;
        const request = `${this.exportsEndpoint}/${exportJobId}`;
        return this.http.delete<boolean>(request).pipe(mapTo(true));
    }

    public bulkDeleteExportJobs(toDelete: ExportJob[]): Observable<boolean[]> {
        const requests$: Observable<boolean>[] = toDelete.map(this.deleteExportJob.bind(this));

        return makeSequentialRequests(requests$);
    }

    public getDownloadStatus(exportJobId: string): Observable<ExportJob> {
        return this.getExportJob(exportJobId).pipe(
            expand((result: ExportJob) => {
                if (result.Status === ExportStatus.FAILED) {
                    throw new Error('Export job failed');
                }
                if (result.DownloadUrl) {
                    return EMPTY;
                }
                return this.getExportJob(exportJobId).pipe(delay(1000));
            }),
            filter((result: ExportJob) => !!result.DownloadUrl)
        );
    }
}
